import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { CheckCircle2, Phone, MapPin, Clock, ChevronRight, ArrowRight } from "lucide-react";
import { api, resolveImg } from "@/lib/api";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";

const SITE = "https://simserv.ro";
const VALUES = [
  "Diagnoză computerizată multimarcă",
  "Decontare directă cu asigurătorii",
  "Piese cu garanție și manoperă certificată",
  "Echipă de tehnicieni cu experiență",
];

export default function AboutPage() {
  const [content, setContent] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    api.get("/content").then((r) => setContent(r.data)).catch(() => {});
  }, []);

  if (!content)
    return <div className="min-h-screen flex items-center justify-center text-[#0F1E36]">Se încarcă...</div>;

  const about = content.settings.about || {};
  const company = content.settings.company || {};
  const stats = about.stats || [];
  const phone = (company.phone || "").replace(/\s/g, "");
  const desc = about.text || "Service auto complet în Slobozia: mecanică, tinichigerie, vopsitorie, ITP, diagnoză și piese.";

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "AutoRepair",
    name: company.name || "SIMSERV Auto Service",
    description: desc,
    url: `${SITE}/despre`,
    telephone: company.phone || "",
    areaServed: { "@type": "City", name: "Slobozia" },
    address: {
      "@type": "PostalAddress",
      streetAddress: company.address || "",
      addressLocality: "Slobozia",
      addressRegion: "Ialomița",
      addressCountry: "RO",
    },
  };

  return (
    <div className="bg-white">
      <Helmet>
        <title>Despre Noi | SIMSERV Auto Service Slobozia</title>
        <meta name="description" content={desc} />
        <link rel="canonical" href={`${SITE}/despre`} />
        <meta property="og:title" content="Despre Noi | SIMSERV Auto Service Slobozia" />
        <meta property="og:description" content={desc} />
        <meta property="og:url" content={`${SITE}/despre`} />
        <meta property="og:type" content="website" />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>
      <Navbar company={company} />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14">
        <nav className="flex items-center gap-1.5 text-sm text-slate-500 mb-8">
          <Link to="/" className="hover:text-[#FF5A1F]">Acasă</Link>
          <ChevronRight className="w-3.5 h-3.5" />
          <span className="text-[#0F1E36] font-medium">Despre Noi</span>
        </nav>

        {/* Intro */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <div className="relative order-2 lg:order-1">
            {about.image_url && (
              <div className="rounded-2xl overflow-hidden border border-[#E2E8F0] shadow-xl">
                <img src={resolveImg(about.image_url)} alt="Atelier SIMSERV Slobozia" className="w-full h-[380px] object-cover" />
              </div>
            )}
            {stats[0]?.value && (
              <div className="absolute -bottom-6 -right-4 sm:right-6 bg-[#FF5A1F] text-white rounded-2xl px-6 py-4 shadow-xl hidden sm:block">
                <p className="font-display text-3xl font-extrabold leading-none">{stats[0].value}</p>
                <p className="text-xs font-semibold uppercase tracking-wide mt-1 text-white/90">{stats[0].label}</p>
              </div>
            )}
          </div>
          <div className="order-1 lg:order-2">
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-[#E11D2A] mb-3">// Despre Noi</p>
            <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-[#0F1E36] leading-tight">
              {about.heading || "SIMSERV – service auto de încredere în Slobozia"}
            </h1>
            <p className="mt-5 text-base text-slate-600 leading-relaxed">{about.text}</p>
            <ul className="mt-7 space-y-3">
              {VALUES.map((item) => (
                <li key={item} className="flex items-center gap-3 text-[#0F1E36] font-medium">
                  <CheckCircle2 className="w-5 h-5 text-[#16A34A] shrink-0" />
                  {item}
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Stats */}
        {stats.length > 0 && (
          <div className="mt-16 rounded-3xl bg-[#0F1E36] p-8 sm:p-12">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
              {stats.map((st, i) => (
                <div key={i} className="text-center sm:text-left" data-testid={`about-stat-${i}`}>
                  <p className="font-display text-4xl sm:text-5xl font-extrabold text-[#FF5A1F]">{st.value}</p>
                  <p className="mt-2 font-semibold text-white">{st.label}</p>
                  {st.desc && <p className="mt-1 text-sm text-white/50 leading-snug">{st.desc}</p>}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Program & contact */}
        <div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="rounded-2xl border border-[#E2E8F0] p-6">
            <div className="flex items-center gap-2 text-[#0F1E36] font-display text-xl font-bold mb-4">
              <Clock className="w-5 h-5 text-[#FF5A1F]" /> Program
            </div>
            <ul className="space-y-2 text-sm text-slate-600">
              {company.program_weekdays && <li className="flex justify-between gap-4"><span>Luni–Vineri</span><span className="font-semibold text-[#0F1E36]">{company.program_weekdays}</span></li>}
              {company.program_saturday && <li className="flex justify-between gap-4"><span>Sâmbătă</span><span className="font-semibold text-[#0F1E36]">{company.program_saturday}</span></li>}
              {company.program_sunday && <li className="flex justify-between gap-4"><span>Duminică</span><span className="font-semibold text-[#0F1E36]">{company.program_sunday}</span></li>}
              {company.program_itp && <li className="flex justify-between gap-4"><span>ITP</span><span className="font-semibold text-[#0F1E36]">{company.program_itp}</span></li>}
            </ul>
          </div>
          <div className="rounded-2xl border border-[#E2E8F0] p-6">
            <div className="flex items-center gap-2 text-[#0F1E36] font-display text-xl font-bold mb-4">
              <MapPin className="w-5 h-5 text-[#FF5A1F]" /> Unde ne găsești
            </div>
            <p className="text-sm text-slate-600 leading-relaxed">{company.address}</p>
            {phone && (
              <a href={`tel:${phone}`} data-testid="about-call-button" className="inline-flex items-center gap-2 mt-4 bg-[#E11D2A] hover:bg-[#C01820] text-white font-semibold px-5 py-3 rounded-lg transition-colors duration-200">
                <Phone className="w-4 h-4" /> {company.phone}
              </a>
            )}
          </div>
        </div>

        {/* CTA to services */}
        <div className="mt-12 flex flex-wrap items-center gap-4">
          <Link to="/servicii" data-testid="about-services-link" className="inline-flex items-center gap-2 bg-[#FF5A1F] hover:bg-[#E04B14] text-white font-semibold px-6 py-3 rounded-lg transition-colors duration-200">
            Vezi serviciile noastre <ArrowRight className="w-4 h-4" />
          </Link>
          <Link to="/#contact" className="inline-flex items-center gap-2 text-[#0F1E36] font-semibold hover:text-[#FF5A1F] transition-colors duration-200">
            Contactează-ne
          </Link>
        </div>
      </main>

      <Footer company={company} />
    </div>
  );
}
